import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Copy,
  Info,
  Lock,
  MoreVertical,
  Pencil,
  Power,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { DsBadge, DsIcon, DsInput } from "../../../design-system";
import { isDefaultSystemRole, isSystemRole, normalizeRoleWizardType } from "./roleAccessUtils";

/**
 * Roles visible on the page: custom roles always; Employee / Self and Reporting Manager
 * only when "Show default roles" is on; any other system role (Admin…) never.
 */
export function visibleRolesFor(roles, { search, showDefaultRoles }) {
  const q = String(search || "").trim().toLowerCase();
  return (Array.isArray(roles) ? roles : [])
    .filter((r) => (isDefaultSystemRole(r) ? showDefaultRoles : !isSystemRole(r)))
    .filter(
      (r) =>
        !q ||
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.code || "").toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const d = (isDefaultSystemRole(a) ? 0 : 1) - (isDefaultSystemRole(b) ? 0 : 1);
      return d || String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
}

function MenuItem({ icon, label, onClick, disabled, title, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title || undefined}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        danger
          ? "text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.08)]"
          : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
      }`}
    >
      <DsIcon icon={icon} size="sm" />
      {label}
    </button>
  );
}

export default function RoleListView({
  roles,
  visibleRoles,
  search,
  setSearch,
  showDefaultRoles,
  setShowDefaultRoles,
  selectedRoleId,
  onSelectRole,
  isDelegate,
  getBlockers,
  onRoleAction,
}) {
  const defaultCount = (Array.isArray(roles) ? roles : []).filter(isDefaultSystemRole).length;
  const activeCount = visibleRoles.filter((r) => r.active).length;
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu]);

  const openMenu = (e, role) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const width = 220;
    const left = Math.min(rect.right - width, window.innerWidth - width - 8);
    const top = Math.min(rect.bottom + 4, window.innerHeight - 220);
    setMenu({ role, top: Math.max(8, top), left: Math.max(8, left) });
  };

  const run = (kind) => {
    if (!menu?.role) return;
    onRoleAction?.(kind, menu.role);
    setMenu(null);
  };

  const menuRole = menu?.role;
  const menuSystem = isSystemRole(menuRole);
  const menuUsesAccess = menuRole ? menuSystem || normalizeRoleWizardType(menuRole) === "access_based" : false;
  const blockers = menuRole && getBlockers ? getBlockers(menuRole) : {};

  return (
    <section
      aria-label="Roles"
      className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
    >
      <div className="shrink-0 space-y-2.5 border-b border-[hsl(var(--ds-border))] p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Roles</h3>
          <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
            {activeCount} active · {visibleRoles.length} listed
          </span>
        </div>
        <div className="relative">
          <DsIcon
            icon={Search}
            size="sm"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
          />
          <DsInput
            type="search"
            placeholder="Search roles"
            aria-label="Search roles"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        {!isDelegate && defaultCount > 0 ? (
          <label className="flex cursor-pointer items-center justify-between gap-2 text-xs text-[hsl(var(--ds-fg))]">
            <span>
              Show default roles
              <span className="block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Employee / Self and Reporting Manager
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-checked={showDefaultRoles}
              checked={showDefaultRoles}
              onChange={(e) => setShowDefaultRoles(e.target.checked)}
              className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
            />
          </label>
        ) : null}
      </div>

      {visibleRoles.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
            {search ? "No roles match your search." : "No custom roles yet. Use Create role to add one."}
          </p>
        </div>
      ) : (
        <ul className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain p-2" role="listbox" aria-label="Role list">
          {visibleRoles.map((role) => {
            const selected = String(role.id) === String(selectedRoleId);
            const system = isSystemRole(role);
            const accessBased = system || normalizeRoleWizardType(role) === "access_based";
            return (
              <li key={role.id} role="option" aria-selected={selected}>
                <div
                  className={`flex w-full items-stretch gap-0.5 rounded-lg border transition-colors duration-150 motion-reduce:transition-none ${
                    selected
                      ? "border-[hsl(var(--ds-primary)/0.5)] bg-[hsl(var(--ds-primary)/0.08)]"
                      : "border-transparent hover:border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelectRole(role.id)}
                    className="min-w-0 flex-1 px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--ds-ring))]"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          role.active ? "bg-[hsl(var(--ds-success))]" : "bg-[hsl(var(--ds-muted-fg))]"
                        }`}
                        title={role.active ? "Active" : "Inactive"}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
                        {role.name}
                      </span>
                      {role.isOwnRole ? (
                        <DsIcon icon={Lock} size="sm" className="text-[hsl(var(--ds-muted-fg))]" aria-label="Your role" />
                      ) : null}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      <DsBadge tone={system ? "neutral" : "primary"}>{system ? "System" : "Custom"}</DsBadge>
                      <DsBadge tone={accessBased ? "info" : "success"}>
                        {accessBased ? "Access Based" : "Action Based"}
                      </DsBadge>
                      {!role.active ? <DsBadge tone="warning">Inactive</DsBadge> : null}
                      <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        <DsIcon icon={Users} size="sm" />
                        {role.assignedUserCount ?? 0}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    aria-label={`Actions for ${role.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuRole && String(menuRole.id) === String(role.id)}
                    onClick={(e) => openMenu(e, role)}
                    className="m-1 flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-md text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                  >
                    <DsIcon icon={MoreVertical} size="sm" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {menu && menuRole
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[12360]" aria-hidden onClick={() => setMenu(null)} />
              <div
                ref={menuRef}
                className="fixed z-[12361] w-[220px] overflow-hidden rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-[var(--ds-shadow-card)]"
                style={{ top: menu.top, left: menu.left }}
                role="menu"
                aria-label={`Actions for ${menuRole.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem icon={Info} label="Info" onClick={() => run("info")} />
                <MenuItem
                  icon={Pencil}
                  label="Edit role"
                  disabled={!!blockers.edit}
                  title={blockers.edit || undefined}
                  onClick={() => run("edit")}
                />
                {menuUsesAccess && !menuSystem ? (
                  <MenuItem
                    icon={Copy}
                    label="Copy access to new role"
                    disabled={!!blockers.copy}
                    title={blockers.copy || undefined}
                    onClick={() => run("copy")}
                  />
                ) : null}
                {!menuSystem ? (
                  <MenuItem
                    icon={Power}
                    label={menuRole.active ? "Deactivate" : "Reactivate"}
                    disabled={!!blockers.toggle}
                    title={blockers.toggle || undefined}
                    onClick={() => run("toggle")}
                  />
                ) : null}
                {!menuSystem && menuRole.deletable !== false ? (
                  <MenuItem
                    icon={Trash2}
                    label="Delete role"
                    danger
                    disabled={!!blockers.delete}
                    title={blockers.delete || undefined}
                    onClick={() => run("delete")}
                  />
                ) : null}
              </div>
            </>,
            document.body
          )
        : null}
    </section>
  );
}
